import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    sendEmailVerification,
    applyActionCode,
    verifyPasswordResetCode,
    confirmPasswordReset,
    GoogleAuthProvider,
    signInWithPopup,
    signOut,
    User,
    UserCredential,
    onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, firestore } from './firebase';
import { DatabaseUser, DatabaseUserModel } from './DatabaseUser';

// Create a new user in Firebase Authentication
export const registerWithEmail = async (email: string, password: string): Promise<UserCredential> => {
    return createUserWithEmailAndPassword(auth, email, password);
};

// Sign in an existing user
export const signInWithEmail = async (email: string, password: string): Promise<UserCredential> => {
    return signInWithEmailAndPassword(auth, email, password);
};

// Sign in (or sign up) with Google via a popup. Google accounts arrive already
// email-verified, so they pass the AuthGate verification gate straight through.
// Ensures the Firestore user doc exists (createUserDocument is idempotent), so a
// first-time Google user is provisioned just like an email registration.
const googleProvider = new GoogleAuthProvider();
export const signInWithGoogle = async (): Promise<UserCredential> => {
    const credential = await signInWithPopup(auth, googleProvider);
    await createUserDocument(credential.user);
    return credential;
};

// Send a password reset email to an existing user
export const sendPasswordReset = async (email: string): Promise<void> => {
    return sendPasswordResetEmail(auth, email);
};

// Send a verification email to a freshly-signed-in / just-registered user
export const sendVerificationEmail = async (user: User): Promise<void> => {
    return sendEmailVerification(user);
};

// Apply the code from an emailed verification link (/auth/action). If this
// browser is signed in as that user, reload it so the next AuthGate load sees
// the verified state without the "I've verified" step.
export const confirmEmailVerification = async (oobCode: string): Promise<void> => {
    await applyActionCode(auth, oobCode);
    await auth.authStateReady();
    await auth.currentUser?.reload();
};

// Check the code from an emailed password-reset link; resolves to the
// account's email, rejects if the link is expired or already used.
export const checkPasswordResetCode = async (oobCode: string): Promise<string> => {
    return verifyPasswordResetCode(auth, oobCode);
};

// Set a new password using the code from an emailed password-reset link
export const resetPassword = async (oobCode: string, newPassword: string): Promise<void> => {
    return confirmPasswordReset(auth, oobCode, newPassword);
};

// Reload the current user's auth record (refreshes emailVerified after the user
// clicks the link in another tab). Returns true once the email is verified.
export const reloadCurrentUser = async (): Promise<boolean> => {
    if (!auth.currentUser) return false;
    await auth.currentUser.reload();
    return auth.currentUser.emailVerified;
};

// Sign out the current user
export const signOutUser = async (): Promise<void> => {
    return signOut(auth);
};

// Create a new user document in Firestore
export const createUserDocument = async (user: User): Promise<void> => {
    if (!user) return;

    const userRef = doc(firestore, 'users', user.uid);
    const snapshot = await getDoc(userRef);

    // Only create the document if it doesn't exist
    if (!snapshot.exists()) {

        const createdAt = new Date();

        const userData: DatabaseUser = {
            createdAt,
            email: user.email!,
            subscription: {
                id: '',
                status: '',
                cancelAtPeriodEnd: false,
                currentPeriodEnd: null,
                interval: null,
            },
            ownedSoundPacks: [],
            cartItems: [],
            registeredDevices: {},
            userTags: []
        };

        try {
            await setDoc(userRef, DatabaseUserModel.toFirestore(userData));
        } catch (error) {
            console.error('Error creating user document', error);
        }
    }
};

// Listen to changes on a user document
export const listenToUserDocument = (userId: string, callback: (data: DatabaseUser | null) => void) => {
    const userRef = doc(firestore, 'users', userId);
    return onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
            try {
                callback(DatabaseUserModel.fromFirestore(docSnap.data()));
            } catch (e) {
                console.error('Invalid user document structure:', e);
                callback(null);
            }
        } else {
            callback(null);
        }
    });
};

// Listen to auth state changes
export const listenToAuthState = (callback: (user: User | null) => void) => {
    return onAuthStateChanged(auth, callback);
};
